/**
 * TestOps Command Context — host-provided dependencies.
 *
 * The package is mounted by an outer ytest product (Athena, Toucan, Admin).
 * Each product injects the bits that vary between hosts:
 *   - the assistant's visual avatar (purple owl in Athena, toucan in Toucan, ...)
 *   - the screenshot renderer (each product knows its own S3 / auth pattern)
 *   - the portfolios fetcher (Knowledge Hub is athena-anchored today)
 *   - the execution-status fetcher (used by ExecutionStatusCard)
 *   - the auth shape (current user + tenant info)
 *
 * Components inside the package consume this via `useTestOpsCommandConfig()`.
 * The host wraps `<TestOpsCommand>` once at the route boundary; everything
 * else flows through context.
 */
import { createContext, useContext, type ReactNode } from 'react'

export interface TestOpsCommandUser {
  id: string
  full_name: string | null
  email?: string | null
}

export interface TestOpsCommandTenant {
  id: string
  name: string
}

export interface PortfolioOption {
  id: string
  name: string
  description?: string | null
}

export interface ExecutionStatusResponse {
  status: string
  duration_seconds?: number
}

export interface ScreenshotImageProps {
  src: string
  alt?: string
  className?: string
  // Some hosts pass extra props; allow passthrough
  [key: string]: unknown
}

export interface TestOpsCommandConfig {
  /** Current authenticated user */
  user: TestOpsCommandUser
  /** Current tenant */
  tenant: TestOpsCommandTenant
  /** Visual logo for the assistant avatar. Each ytest product brings its own. */
  AvatarComponent: React.ComponentType<{ size?: number; className?: string }>
  /** Screenshot renderer. Each host knows its own auth + S3 presigning. */
  ScreenshotComponent: React.ComponentType<ScreenshotImageProps>
  /** Fetch the tenant's Knowledge-Hub portfolios. Athena-anchored today. */
  fetchPortfolios: (params?: { limit?: number }) => Promise<PortfolioOption[]>
  /** Fetch a single execution's status by id. Used by ExecutionStatusCard. */
  fetchExecutionStatus: (executionId: string) => Promise<ExecutionStatusResponse>
}

const TestOpsCommandContext = createContext<TestOpsCommandConfig | null>(null)

export function TestOpsCommandProvider({
  config,
  children,
}: {
  config: TestOpsCommandConfig
  children: ReactNode
}) {
  return (
    <TestOpsCommandContext.Provider value={config}>{children}</TestOpsCommandContext.Provider>
  )
}

export function useTestOpsCommandConfig(): TestOpsCommandConfig {
  const ctx = useContext(TestOpsCommandContext)
  if (!ctx) {
    throw new Error(
      '<TestOpsCommand /> components must be rendered inside <TestOpsCommandProvider />. ' +
        'Wrap your route element with <TestOpsCommand /> (the package entry) which sets up the provider.'
    )
  }
  return ctx
}
